import { v4 as uuid } from 'uuid'
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react'
import { Alert, Card, Empty, Form, Input, Modal, Segmented, Select, Tag, Typography } from 'antd'
import { Background, Controls, Handle, MiniMap, Position, ReactFlow, applyNodeChanges,
  type Connection, type Edge, type Node, type NodeChange, type NodeMouseHandler, type NodeProps, type OnNodeDrag } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import type { useWorkflows } from '../hooks/useWorkflows'
import { Icon } from './Icon'
import { StepForm, type StepDraft } from './StepForm'
import { CanvasToolbar } from './CanvasToolbar'
import { WorkflowLoop } from './WorkflowLoop'

type StepNode = Node<{ title: string; operation: string; iconKey: string; entry?: boolean }, 'step'>
const CANVAS_STYLE = { height: 560, minHeight: 360, width: '100%' }
const FIT_OPTIONS = { padding: 0.25, maxZoom: 1 }
const VIEWS = [{ label: '流程画布', value: 'canvas' }, { label: '源码', value: 'source' }]
const DEFAULT_EDGE = { type: 'smoothstep', style: { stroke: 'var(--xiaocai-brand-primary)', strokeWidth: 2 } }
const TITLE_RULES = [{ required: true, whitespace: true, message: '请输入流程名称' }]
const Step = ({ data, selected }: NodeProps<StepNode>) => <div className={`instance-administration__workflow-step${selected ? ' is-selected' : ''}`}>
  {!data.entry ? <Handle type="target" position={Position.Top} /> : null}<div className="instance-administration__workflow-step-icon"><Icon iconKey={data.iconKey} /></div>
  <div><strong>{data.title}</strong><small>{data.operation}</small></div><Handle type="source" position={Position.Bottom} isConnectable={!data.entry} />
</div>
const NODE_TYPES = { step: Step }
const EDGE_TYPES = { loop: WorkflowLoop }

/** Only gestures are local. Workflow definitions and activation have separate owners. */
export const WorkflowEditor = ({ model, disabled = false }: { model: ReturnType<typeof useWorkflows>; disabled?: boolean }) => {
  const flow = model.selected
  const [view, setView] = useState('canvas')
  const [editing, setEditing] = useState<StepDraft | null>(null)
  const [metadataOpen, setMetadataOpen] = useState(false)
  const [reloadOpen, setReloadOpen] = useState(false)
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null)
  const [selectedNode, setSelectedNode] = useState<string | null>(null)
  const canvas = useRef<HTMLDivElement>(null)
  // An accepted native entry policy is presentation, not a callable capability.
  const entryId = useRef(uuid()).current
  const [form] = Form.useForm<{ title: string; description: string; entryStepId: string | null }>()
  const operations = useMemo(() => model.data?.operations ?? [], [model.data?.operations])
  const byId = useMemo(() => new Map(operations.map((item) => [item.operation_id, item])), [operations])
  const projected = useMemo<StepNode[]>(() => [...(flow?.entry_policy_ref ? [{ id: entryId, type: 'step' as const,
    data: { title: '意图识别', operation: '', iconKey: '', entry: true }, position: { x: 80, y: -100 },
    deletable: false, draggable: false, selectable: false, ariaLabel: '意图识别' }] : []), ...(flow?.steps.map((step) => ({
    id: step.step_id, type: 'step' as const, data: { title: step.title, operation: byId.get(step.operation_id)?.label ?? '业务操作不可用', iconKey: byId.get(step.operation_id)?.icon_key ?? '' },
    position: step.position, deletable: false, ariaLabel: step.title,
  })) ?? [])], [byId, entryId, flow])
  const edges = useMemo<Edge[]>(() => [...(flow?.entry_policy_ref && flow.entry_step_id ? [{
    id: entryId, source: entryId, target: flow.entry_step_id, selectable: false, deletable: false,
  }] : []), ...(flow?.transitions.map((edge) => {
    const id = JSON.stringify([edge.source, edge.target, edge.event])
    return { ...edge, id, selected: selectedEdge?.id === id,
      type: edge.source === edge.target ? 'loop' : 'smoothstep', label: edge.label, deletable: false }
  }) ?? [])], [entryId, flow, selectedEdge?.id])
  const [nodes, setNodes] = useState<StepNode[]>(projected)
  useEffect(() => setNodes(projected), [projected])
  const scope = `${model.instanceId}:${flow?.workflow_id ?? ''}`
  useEffect(() => { setEditing(null); setSelectedNode(null); setSelectedEdge(null); setMetadataOpen(false) }, [scope])
  const { edit } = model
  const locked = disabled || model.pending
  const nodeChanges = useCallback((changes: NodeChange<StepNode>[]) => setNodes((previous) => applyNodeChanges(changes, previous)), [])
  const connect = useCallback((connection: Connection) => {
    if (!locked && connection.source && connection.target) edit({ kind: 'connect', source: connection.source, target: connection.target })
  }, [edit, locked])
  const dragged = useCallback<OnNodeDrag<StepNode>>((_event, _node, draggedNodes) => {
    if (!locked) edit({ kind: 'positions', positions: Object.fromEntries(draggedNodes.map((item) => [item.id, item.position])) })
  }, [edit, locked])
  const openStep = useCallback((id: string) => {
    const step = flow?.steps.find((item) => item.step_id === id)
    if (step && !locked) setEditing({ kind: 'update', id, step })
  }, [flow, locked])
  const pickNode = useCallback<NodeMouseHandler<StepNode>>((_event, node) => {
    if (node.data.entry) return
    setSelectedNode(node.id); setSelectedEdge(null); canvas.current?.focus({ preventScroll: true })
  }, [])
  const doubleClick = useCallback<NodeMouseHandler<StepNode>>((_event, node) => openStep(node.id), [openStep])
  const addStep = useCallback(() => setEditing({ kind: 'add', id: uuid() }), [])
  const closeStep = useCallback(() => setEditing(null), [])
  const removeStep = useCallback(() => { if (selectedNode) { edit({ kind: 'remove', id: selectedNode }); setSelectedNode(null) } }, [edit, selectedNode])
  const pickEdge = useCallback((_event: MouseEvent, edge: Edge) => {
    if (edge.selectable === false) return
    setSelectedEdge(edge); setSelectedNode(null); canvas.current?.focus({ preventScroll: true })
  }, [])
  const clear = useCallback(() => { setSelectedEdge(null); setSelectedNode(null) }, [])
  const disconnect = useCallback(() => { if (selectedEdge) edit({ kind: 'disconnect', source: selectedEdge.source, target: selectedEdge.target }); setSelectedEdge(null) }, [edit, selectedEdge])
  // Keep deletion inside the focused canvas; ReactFlow's document-wide hotkey
  // remains disabled so typing or a modal cannot mutate the workflow draft.
  const deleteSelection = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (!['Delete', 'Backspace'].includes(event.key) || event.defaultPrevented || event.repeat
      || event.altKey || event.ctrlKey || event.metaKey || locked || editing || metadataOpen || reloadOpen) return
    if (event.target instanceof Element && event.target.closest('input, textarea, select, [role="textbox"], [role="combobox"], [contenteditable]:not([contenteditable="false"])')) return
    if (!selectedNode && !selectedEdge) return
    event.preventDefault(); event.stopPropagation()
    if (selectedNode) removeStep()
    else disconnect()
  }, [disconnect, editing, locked, metadataOpen, reloadOpen, removeStep, selectedEdge, selectedNode])
  const metadata = useCallback(() => { form.setFieldsValue({ title: flow?.title, description: flow?.description, entryStepId: flow?.entry_step_id }); setMetadataOpen(true) }, [flow, form])
  const closeMetadata = useCallback(() => setMetadataOpen(false), [])
  const submitMetadata = useCallback(async () => {
    const fields = await form.validateFields().catch(() => null)
    if (fields && edit({ kind: 'metadata', ...fields, entryStepId: fields.entryStepId ?? undefined }).kind === 'accepted') setMetadataOpen(false)
  }, [edit, form])
  const save = useCallback(() => { void model.save().catch(() => undefined) }, [model])
  const activate = useCallback(() => { void model.activate().catch(() => undefined) }, [model])
  const reload = useCallback(() => {
    if (model.changed) { setReloadOpen(true); return }
    void model.reload().catch(() => undefined)
  }, [model])
  const closeReload = useCallback(() => setReloadOpen(false), [])
  const confirmReload = useCallback(async () => {
    await model.reload()
    model.discard()
    setReloadOpen(false)
  }, [model])
  const remove = useCallback(() => { void model.remove().catch(() => undefined) }, [model])
  const entryOptions = useMemo(() => flow?.steps.map((item) => ({ value: item.step_id, label: item.title })) ?? [], [flow?.steps])
  const changeView = useCallback((value: string | number) => setView(String(value)), [])
  const heading = <div className="instance-administration__file-heading"><Typography.Text strong>{flow?.title ?? '流程编排'}</Typography.Text>
    {flow?.workflow_id === model.data?.active_workflow_id ? <Tag color="success">当前启用</Tag> : null}
    {model.changed ? <Tag color="processing">有未保存修改</Tag> : null}</div>
  return <Card title={heading} className="instance-administration__file-editor" loading={model.loading && !model.data}>
    {model.error ? <Alert title={model.error} type="error" role="alert" showIcon /> : null}
    {flow ? <>
      <div className="instance-administration__workflow-navigation"><Segmented options={VIEWS} value={view} onChange={changeView} aria-label="编辑方式" />
        <CanvasToolbar disabled={locked} canAdd={operations.length > 0} changed={model.changed} canActivate={model.data?.activation_required_workflow_ids?.includes(flow.workflow_id) === true}
          onAdd={addStep} onMetadata={metadata} onSave={save} onActivate={activate} onReload={reload} onDelete={remove} /></div>
      {flow.description ? <Typography.Paragraph type="secondary">{flow.description}</Typography.Paragraph> : null}
      {view === 'source' ? <pre aria-label="业务流程源码">{JSON.stringify(flow, null, 2)}</pre> : <div ref={canvas} role="region" aria-label="流程画布" tabIndex={0}
        onKeyDown={deleteSelection} className="instance-administration__workflow-canvas" style={CANVAS_STYLE}>
        <ReactFlow<StepNode, Edge> key={scope} nodes={nodes} edges={edges} nodeTypes={NODE_TYPES} edgeTypes={EDGE_TYPES} onNodesChange={nodeChanges}
          onConnect={connect} onNodeDragStop={dragged} onNodeClick={pickNode} onNodeDoubleClick={doubleClick} onEdgeClick={pickEdge} onPaneClick={clear}
          nodesDraggable={!locked} nodesConnectable={!locked} deleteKeyCode={null} defaultEdgeOptions={DEFAULT_EDGE}
          fitView fitViewOptions={FIT_OPTIONS} minZoom={0.2} maxZoom={2}>
          <Background gap={20} size={1} /><Controls showInteractive={false} /><MiniMap pannable zoomable />
        </ReactFlow>
        {!flow.steps.length ? <div className="instance-administration__workflow-empty"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="暂无步骤，点击“新增步骤”开始配置" /></div> : null}
      </div>}
    </> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="请在左侧选择或新增业务流程" />}
    {editing ? <StepForm key={scope + editing.id} draft={editing} operations={operations} disabled={locked} onSubmit={edit} onClose={closeStep} /> : null}
    <Modal title="重新读取流程" open={reloadOpen} onCancel={closeReload} onOk={confirmReload} okText="放弃修改并读取" cancelText="取消">
      重新读取会放弃当前流程未保存的修改，是否继续？
    </Modal>
    <Modal title="流程信息" open={metadataOpen} onCancel={closeMetadata} onOk={submitMetadata} okText="确定" cancelText="取消">
      <Form form={form} layout="vertical"><Form.Item name="title" label="流程名称" rules={TITLE_RULES}><Input placeholder="请输入流程名称" maxLength={200} /></Form.Item>
        <Form.Item name="entryStepId" label="起始步骤"><Select options={entryOptions} placeholder="请选择流程的起始步骤" /></Form.Item>
        <Form.Item name="description" label="流程说明"><Input.TextArea placeholder="请输入此流程处理的业务及预期结果" maxLength={2000} /></Form.Item></Form>
    </Modal>
  </Card>
}
